/**
 * Supabase Storage Image Transformation helper.
 *
 * Rewrites public Storage URLs from the raw `/object/public/` endpoint to the
 * on-the-fly resizer at `/render/image/public/`, so we ship correctly-sized
 * images instead of the original (often 1024–2048px wide) source.
 *
 * Non-Supabase URLs and URLs that are already pointing to `/render/image/`
 * are returned unchanged. Failures are silently passed through – the worst
 * case is the original URL being served.
 */

const SUPABASE_OBJECT_PATH = '/storage/v1/object/public/';
const SUPABASE_RENDER_PATH = '/storage/v1/render/image/public/';

export interface ImageTransformOptions {
  /** Target rendered width in CSS pixels. We auto-multiply for retina (2x). */
  width?: number;
  /** Target rendered height in CSS pixels. We auto-multiply for retina (2x). */
  height?: number;
  /** JPEG/WebP quality, 20–100. Defaults to 75 (good balance for product art). */
  quality?: number;
  /** How the image is fitted into the box. */
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Returns a transformed Supabase Storage URL or the original URL if no
 * transformation can be applied.
 */
export function transformImage(
  url: string | null | undefined,
  options: ImageTransformOptions = {},
): string {
  if (!url) return '';
  // Only rewrite Supabase public-object URLs; leave everything else alone.
  if (!url.includes(SUPABASE_OBJECT_PATH)) return url;
  // Already a render URL – don't double-transform.
  if (url.includes(SUPABASE_RENDER_PATH)) return url;

  const { width, height, quality = 75, resize } = options;
  if (!width && !height) return url;

  const dpr =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? Math.min(2, Math.max(1, window.devicePixelRatio))
      : 1;

  const params = new URLSearchParams();
  if (width) params.set('width', String(Math.round(width * dpr)));
  if (height) params.set('height', String(Math.round(height * dpr)));
  if (resize) params.set('resize', resize);
  if (quality) params.set('quality', String(quality));

  try {
    const rewritten = url.replace(SUPABASE_OBJECT_PATH, SUPABASE_RENDER_PATH);
    const sep = rewritten.includes('?') ? '&' : '?';
    return `${rewritten}${sep}${params.toString()}`;
  } catch {
    return url;
  }
}
